import React, { useEffect, useMemo, useState } from "react";
import {
  Bell,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Eye,
  FileText,
  ImagePlus,
  Loader2,
  MapPin,
  MessageSquare,
  Search,
  Send,
  Trash2,
  UserRound,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { UserNotificationsService } from "../../../services/userNotifications.service";

const cardClass = "rounded-[22px] border border-[#e5e9f2] bg-white shadow-[0_12px_35px_rgba(37,48,78,0.055)]";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#d86a78] focus:ring-4 focus:ring-[#d86a78]/10";

function getUserLabel(user) {
  return (
    user?.name ||
    user?.email ||
    user?.phone ||
    user?.user_code ||
    user?.id ||
    "User"
  );
}

function normalizeResponseData(res) {
  return res?.data || res || {};
}

function formatDateTime(value) {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return "-";
  }
}

function getStatusClass(status) {
  const s = String(status || "").toUpperCase();

  if (s === "PENDING") return "bg-amber-50 text-amber-700 border-amber-200";
  if (s === "SENT") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s === "COMPLETED") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (s === "FAILED") return "bg-red-50 text-red-700 border-red-200";
  if (s === "CANCELLED") return "bg-slate-100 text-slate-600 border-slate-200";
  if (s === "PROCESSING") return "bg-blue-50 text-blue-700 border-blue-200";

  return "bg-slate-100 text-slate-600 border-slate-200";
}

function getScheduleText({ sendMode, scheduleType, scheduledAt, startDate, endDate, dailyTime }) {
  if (sendMode !== "schedule") return "Send now";

  if (scheduleType === "daily") {
    return `${startDate || "Start date"} to ${endDate || "End date"} at ${
      dailyTime || "time"
    }`;
  }

  return scheduledAt || "Schedule not selected";
}

function StateDropdown({ value, options, loading, onChange }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    function closeOnOutsideClick(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    function closeOnEscape(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const selected = options.find((item) => item.state === value);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={`flex h-12 w-full items-center justify-between rounded-2xl border bg-white px-4 text-left text-sm font-medium text-slate-800 outline-none transition ${
          open
            ? "border-[#e26679] shadow-[0_0_0_4px_rgba(226,102,121,0.10)]"
            : "border-slate-200 hover:border-rose-200"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{selected?.state || "All states"}</span>
        <ChevronDown size={17} className={`shrink-0 text-slate-500 transition-transform duration-200 ${open ? "rotate-180 text-[#d86a78]" : ""}`} />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-2xl border border-rose-100 bg-white p-1.5 shadow-[0_18px_45px_rgba(37,48,78,0.16)]">
          <div role="listbox" className="sidebar-scroll max-h-[220px] space-y-1 overflow-y-auto overscroll-contain">
            <button
              type="button"
              role="option"
              aria-selected={!value}
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className={`flex w-full items-center justify-between rounded-xl px-3.5 py-2.5 text-left text-sm transition ${!value ? "bg-[#fff0f3] font-semibold text-[#d9526a]" : "font-medium text-slate-600 hover:bg-slate-50 hover:text-[#d86a78]"}`}
            >
              <span>All states</span>
              {!value ? <CheckCircle2 size={16} /> : null}
            </button>

            {loading ? (
              <div className="flex items-center justify-center gap-2 px-3 py-4 text-xs font-medium text-slate-400"><Loader2 size={15} className="animate-spin" />Loading states...</div>
            ) : (
              options.map((item) => {
                const active = item.state === value;
                return (
                  <button
                    key={item.state}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => {
                      onChange(item.state);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center justify-between gap-3 rounded-xl px-3.5 py-2.5 text-left text-sm transition ${active ? "bg-[#fff0f3] font-semibold text-[#d9526a]" : "font-medium text-slate-600 hover:bg-slate-50 hover:text-[#d86a78]"}`}
                  >
                    <span className="truncate">{item.state}</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] ${active ? "bg-white text-[#d9526a]" : "bg-slate-100 text-slate-500"}`}>{item.user_count}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function UserNotificationsPage() {
  const [audience, setAudience] = useState("single");
  const [q, setQ] = useState("");
  const [state, setState] = useState("");

  const [states, setStates] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState("");
  const [mediaInputKey, setMediaInputKey] = useState(0);

  const [sendMode, setSendMode] = useState("now");
  const [scheduleType, setScheduleType] = useState("once");
  const [scheduledAt, setScheduledAt] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dailyTime, setDailyTime] = useState("");

  const [scheduledItems, setScheduledItems] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  const [cancelingId, setCancelingId] = useState("");
  const [clearingHistory, setClearingHistory] = useState(false);

  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const titleLeft = Math.max(0, 80 - title.length);
  const bodyLeft = Math.max(0, 240 - body.length);

  const canSend = useMemo(() => {
    if (!title.trim() || !body.trim()) return false;
    if (audience === "single" && !selectedUser?.id) return false;

    if (sendMode === "schedule") {
      if (scheduleType === "once" && !scheduledAt) return false;
      if (scheduleType === "daily" && (!startDate || !endDate || !dailyTime)) return false;
    }

    return true;
  }, [
    audience,
    title,
    body,
    selectedUser,
    sendMode,
    scheduleType,
    scheduledAt,
    startDate,
    endDate,
    dailyTime,
  ]);

  useEffect(() => {
    let alive = true;

    async function loadStates() {
      setLoadingStates(true);

      try {
        const res = await UserNotificationsService.listStates();
        const rows = normalizeResponseData(res);

        if (alive) {
          setStates(Array.isArray(rows) ? rows : []);
        }
      } catch (err) {
        if (alive) {
          console.error("Failed to load states:", err);
        }
      } finally {
        if (alive) setLoadingStates(false);
      }
    }

    loadStates();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    async function loadUsers() {
      if (audience !== "single") return;

      setLoadingUsers(true);
      setError("");

      try {
        const res = await UserNotificationsService.listPushUsers({
          q,
          state,
          page: 1,
          limit: 20,
        });

        const data = normalizeResponseData(res);
        const items = data?.items || [];

        if (alive) {
          setUsers(Array.isArray(items) ? items : []);
        }
      } catch (err) {
        if (alive) setError(err?.message || "Failed to load users");
      } finally {
        if (alive) setLoadingUsers(false);
      }
    }

    const timer = setTimeout(loadUsers, 350);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [q, state, audience]);

  async function loadScheduledItems() {
    setLoadingScheduled(true);

    try {
      const res = await UserNotificationsService.listScheduled({ limit: 50 });
      const data = normalizeResponseData(res);
      setScheduledItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load scheduled notifications:", err);
    } finally {
      setLoadingScheduled(false);
    }
  }

  useEffect(() => {
    loadScheduledItems();
  }, []);

  useEffect(() => {
    return () => {
      if (mediaPreview) URL.revokeObjectURL(mediaPreview);
    };
  }, [mediaPreview]);

  function clearMedia() {
    setMediaFile(null);
    setMediaPreview("");
    setMediaInputKey((value) => value + 1);
  }

  function handleMediaChange(event) {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    const allowedTypes = new Set([
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
    ]);

    if (!allowedTypes.has(file.type)) {
      setError("Use a JPG, PNG, WEBP, or GIF file");
      setMediaInputKey((value) => value + 1);
      return;
    }

    if (file.size > 1024 * 1024) {
      setError("Notification image or GIF must be 1 MB or smaller");
      setMediaInputKey((value) => value + 1);
      return;
    }

    setError("");
    setResult(null);
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
  }

  function resetResult() {
    setResult(null);
    setError("");
  }

  function resetScheduleFields() {
    setScheduleType("once");
    setScheduledAt("");
    setStartDate("");
    setEndDate("");
    setDailyTime("");
  }

  async function handleCancelScheduled(id) {
    if (!id || cancelingId) return;

    setCancelingId(id);
    setError("");

    try {
      await UserNotificationsService.cancelScheduled(id);
      await loadScheduledItems();
    } catch (err) {
      setError(err?.message || "Failed to cancel scheduled notification");
    } finally {
      setCancelingId("");
    }
  }

  async function handleClearHistory() {
    const ok = window.confirm(
      "Clear old scheduled notification history? Pending and processing notifications will not be deleted."
    );

    if (!ok) return;

    setClearingHistory(true);
    setError("");

    try {
      await UserNotificationsService.clearScheduledHistory();
      await loadScheduledItems();
    } catch (err) {
      setError(err?.message || "Failed to clear scheduled history");
    } finally {
      setClearingHistory(false);
    }
  }

  async function handleSend() {
    if (!canSend || sending) return;

    setSending(true);
    setError("");
    setResult(null);

    try {
      const payload = {
        audience,
        title: title.trim(),
        body: body.trim(),
      };

      if (state) {
        payload.state = state;
      }

      if (audience === "single") {
        payload.userId = selectedUser.id;
      }

      let res;

      if (sendMode === "schedule") {
        if (scheduleType === "daily") {
          res = await UserNotificationsService.scheduleNotification(
            {
              ...payload,
              scheduleType: "DAILY",
              startDate,
              endDate,
              dailyTime,
              timezone: "Asia/Kolkata",
            },
            mediaFile
          );
        } else {
          const scheduleDate = new Date(scheduledAt);

          if (Number.isNaN(scheduleDate.getTime())) {
            throw new Error("Please select a valid schedule date and time");
          }

          res = await UserNotificationsService.scheduleNotification(
            {
              ...payload,
              scheduleType: "ONCE",
              scheduledAt: scheduleDate.toISOString(),
              timezone: "Asia/Kolkata",
            },
            mediaFile
          );
        }
      } else {
        res = await UserNotificationsService.sendNotification(payload, mediaFile);
      }

      const data = normalizeResponseData(res);

      setResult(data);
      setTitle("");
      setBody("");
      setSelectedUser(null);
      setQ("");
      clearMedia();
      resetScheduleFields();

      if (sendMode === "schedule") {
        await loadScheduledItems();
      }
    } catch (err) {
      setError(err?.message || "Notification send failed");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="user-notifications-page w-full space-y-4">
      <div className="w-full space-y-4">
        <div className="relative overflow-hidden rounded-[24px] border border-rose-100 bg-[linear-gradient(110deg,#ffffff_0%,#fffefe_62%,#fff1f5_100%)] px-5 py-4 shadow-[0_12px_36px_rgba(216,106,120,0.07)] sm:px-6">
          <div className="pointer-events-none absolute -right-10 -top-20 h-52 w-52 rounded-full bg-rose-200/25 blur-3xl" />
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-black text-[#d86a78] shadow-sm ring-1 ring-[#f2c6cc]">
            <Bell size={16} />
            User Notifications
          </div>

          <div className="relative z-10 mt-3 flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-[25px] font-black tracking-tight text-[#dc667b] sm:text-[29px]">
                Send a mobile notification
              </h1>
              <p className="mt-1 max-w-2xl text-[13px] font-semibold text-slate-500">
                Create a short message and send it now, schedule it once, or send it daily in a date range.
              </p>
            </div>
          </div>

          <div className="pointer-events-none absolute bottom-0 right-8 hidden h-[112px] w-[310px] lg:block">
            <div className="absolute bottom-[-34px] left-7 h-[128px] w-[76px] rounded-t-[22px] border-[5px] border-[#15254b] bg-[linear-gradient(160deg,#f7bfd0,#c8c1f5)] shadow-xl">
              <div className="mx-auto mt-1 h-1.5 w-7 rounded-full bg-[#15254b]" />
            </div>
            <div className="absolute right-0 top-5 flex w-[220px] items-center gap-3 rounded-[16px] border border-white/90 bg-white/95 p-3 shadow-[0_14px_35px_rgba(80,52,83,0.12)]">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-rose-100 text-[#ef5570]"><Bell size={22} fill="currentColor" /></div>
              <div><div className="text-[13px] font-black text-[#101b35]">Stay Connected</div><div className="mt-0.5 text-[11px] font-semibold text-slate-500">Send important updates to your users</div></div>
            </div>
          </div>
        </div>

        {error ? (
          <div className="mb-5 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
            <XCircle size={18} />
            <span>{error}</span>
          </div>
        ) : null}

        {result ? (
          <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-800">
            <div className="flex items-center gap-2 font-black">
              <CheckCircle2 size={18} />
              {sendMode === "schedule"
                ? "Notification scheduled successfully"
                : "Notification sent successfully"}
            </div>

            {sendMode === "schedule" ? (
              <div className="mt-3 grid gap-2 text-xs font-bold sm:grid-cols-4">
                <div>Status: {result?.status || "PENDING"}</div>
                <div>Type: {result?.scheduleType || scheduleType}</div>
                <div>Targets: {result?.estimatedTargetCount ?? 0}</div>
                <div>Next: {formatDateTime(result?.nextRunAt || result?.scheduledAt)}</div>
              </div>
            ) : (
              <div className="mt-3 grid gap-2 text-xs font-bold sm:grid-cols-4">
                <div>Targets: {result?.targetCount ?? 0}</div>
                <div>Saved: {result?.notificationRows ?? 0}</div>
                <div>Sent: {result?.pushSent ?? 0}</div>
                <div>Failed: {result?.pushFailed ?? 0}</div>
              </div>
            )}
          </div>
        ) : null}

        <div className="grid w-full gap-3 xl:grid-cols-[320px_minmax(0,1fr)] 2xl:grid-cols-[340px_minmax(0,1fr)]">
          <section className={`${cardClass} flex min-h-[620px] flex-col p-4`}>
            <div className="shrink-0">
              <div className="flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-full bg-rose-50 text-[#ef5570]"><UserRound size={19} /></span><div><h2 className="text-[17px] font-black text-[#101b35]">Recipients</h2><p className="text-[12px] font-semibold text-slate-500">Choose who should receive this notification.</p></div></div>
            </div>

            <div className="mt-5 grid shrink-0 grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setAudience("single");
                  setSelectedUser(null);
                  resetResult();
                }}
                className={[
                  "rounded-2xl border p-4 text-left transition",
                  audience === "single"
                    ? "border-[#d86a78] bg-[#fff0f2] text-[#d86a78]"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                ].join(" ")}
              >
                <UserRound size={22} />
                <div className="mt-3 text-sm font-black">Single user</div>
                <div className="mt-1 text-xs font-semibold opacity-70">Send to one user</div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setAudience("all");
                  setSelectedUser(null);
                  resetResult();
                }}
                className={[
                  "rounded-2xl border p-4 text-left transition",
                  audience === "all"
                    ? "border-[#d86a78] bg-[#fff0f2] text-[#d86a78]"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                ].join(" ")}
              >
                <Users size={22} />
                <div className="mt-3 text-sm font-black">All users</div>
                <div className="mt-1 text-xs font-semibold opacity-70">Send to many users</div>
              </button>
            </div>

            <div className="mt-5 shrink-0">
              <label className="mb-2 flex items-center gap-2 text-sm font-black text-slate-800">
                <MapPin size={16} />
                State filter
              </label>

              <StateDropdown
                value={state}
                options={states}
                loading={loadingStates}
                onChange={(nextState) => {
                  setState(nextState);
                  setSelectedUser(null);
                  resetResult();
                }}
              />

              {loadingStates ? (
                <p className="mt-2 text-xs font-bold text-slate-400">Loading states...</p>
              ) : (
                <p className="mt-2 text-xs font-bold text-slate-400">
                  Leave empty to include users from every state.
                </p>
              )}
            </div>

            {audience === "single" ? (
              <div className="mt-6 flex min-h-0 flex-1 flex-col">
                <label className="shrink-0 text-sm font-black text-slate-800">
                  Search user
                </label>

                <div className="relative mt-2 shrink-0">
                  <Search
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      resetResult();
                    }}
                    placeholder="Search by name, email, phone, or user code"
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm font-semibold outline-none transition focus:border-[#d86a78] focus:ring-4 focus:ring-[#d86a78]/10"
                  />
                </div>

                <div className="mt-4 min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain pr-1">
                  {loadingUsers ? (
                    <div className="flex items-center justify-center rounded-2xl border border-dashed border-slate-200 py-8 text-sm font-bold text-slate-400">
                      <Loader2 className="mr-2 animate-spin" size={18} />
                      Loading users...
                    </div>
                  ) : users.length ? (
                    users.map((user) => {
                      const active = selectedUser?.id === user.id;

                      return (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => {
                            setSelectedUser(user);
                            resetResult();
                          }}
                          className={[
                            "w-full rounded-2xl border p-4 text-left transition",
                            active
                              ? "border-[#d86a78] bg-[#fff0f2]"
                              : "border-slate-200 bg-white hover:bg-slate-50",
                          ].join(" ")}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate font-black text-slate-900">
                                {getUserLabel(user)}
                              </div>

                              <div className="mt-1 truncate text-xs font-semibold text-slate-500">
                                {user.email || "No email"} • {user.phone || "No phone"}
                              </div>

                              <div className="mt-1 truncate text-xs font-semibold text-slate-400">
                                {[user.city, user.state].filter(Boolean).join(", ") ||
                                  "No location"}
                              </div>

                              <div className="mt-1 truncate text-xs font-semibold text-slate-400">
                                {user.user_code || user.id}
                              </div>
                            </div>

                            <div className="shrink-0 rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-500">
                              {user.token_count || 0} token
                            </div>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-200 py-8 text-center text-sm font-bold text-slate-400">
                      No user with an active device token found
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-6 rounded-2xl bg-[#fff0f2] p-4 text-sm font-bold text-[#bf4f5f]">
                This message will be sent to all users with active device tokens
                {state ? ` in ${state}.` : "."}
              </div>
            )}
          </section>

          <section className="grid min-w-0 gap-3 2xl:grid-cols-[minmax(0,1fr)_320px]">
            <div className={`${cardClass} min-w-0 p-5`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-emerald-50 text-emerald-600"><MessageSquare size={19} /></span>
                  <div><h2 className="text-[17px] font-black text-[#101b35]">Notification message</h2><p className="text-[12px] font-semibold text-slate-500">Keep the message short, clear, and useful.</p></div>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full border border-rose-100 bg-rose-50 px-3 py-2 text-[11px] font-black text-[#df536a]"><FileText size={14} />Templates<ChevronDown size={13} /></div>
              </div>

              <div className="mt-5">
              <div className="space-y-4">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-sm font-black text-slate-800">Title</label>
                    <span className="text-xs font-bold text-slate-400">
                      {titleLeft} left
                    </span>
                  </div>

                  <input
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value.slice(0, 80));
                      resetResult();
                    }}
                    placeholder="Example: Special offer"
                    className={inputClass}
                  />
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-sm font-black text-slate-800">Message</label>
                    <span className="text-xs font-bold text-slate-400">
                      {bodyLeft} left
                    </span>
                  </div>

                  <textarea
                    value={body}
                    onChange={(e) => {
                      setBody(e.target.value.slice(0, 240));
                      resetResult();
                    }}
                    placeholder="Example: Your special offer is live now."
                    rows={4}
                    className={`${inputClass} resize-none`}
                  />

                  <div className="mt-2 flex flex-wrap gap-2">
                    {[
                      ["Offer", "Special offer", "Your special offer is live now."],
                      ["Update", "Important update", "We have an important update for you."],
                      ["Reminder", "Friendly reminder", "This is a quick reminder from NefaZo."],
                      ["New Feature", "Something new", "A new feature is now available for you."],
                      ["Maintenance", "Maintenance update", "Scheduled maintenance information for our users."],
                    ].map(([label, nextTitle, nextBody]) => (
                      <button key={label} type="button" onClick={() => { setTitle(nextTitle); setBody(nextBody); resetResult(); }} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-black text-slate-600 transition hover:border-rose-200 hover:bg-rose-50 hover:text-[#d86a78]">
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label className="text-sm font-black text-slate-800">
                      Notification image / GIF
                    </label>
                    <span className="text-xs font-bold text-slate-400">Optional · max 1 MB</span>
                  </div>

                  {mediaPreview ? (
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                      <div className="relative aspect-[16/7] bg-slate-100">
                        <img
                          src={mediaPreview}
                          alt="Notification media preview"
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            clearMedia();
                            resetResult();
                          }}
                          className="absolute right-3 top-3 inline-flex items-center gap-2 rounded-xl bg-white/95 px-3 py-2 text-xs font-black text-red-600 shadow-sm ring-1 ring-slate-200 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                          Remove
                        </button>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                        <div className="min-w-0">
                          <div className="truncate text-xs font-black text-slate-700">
                            {mediaFile?.name || "Notification media"}
                          </div>
                          <div className="mt-1 text-[11px] font-bold text-slate-400">
                            {mediaFile?.type === "image/gif" ? "GIF" : "Image"} · {mediaFile ? `${(mediaFile.size / 1024).toFixed(0)} KB` : ""}
                          </div>
                        </div>

                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-50">
                          <ImagePlus size={15} />
                          Change
                          <input
                            key={`change-${mediaInputKey}`}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            onChange={handleMediaChange}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <label className="flex cursor-pointer items-center gap-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 transition hover:border-[#d86a78] hover:bg-[#fff7f8]">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-[#d86a78] shadow-sm ring-1 ring-slate-200">
                        <ImagePlus size={21} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-black text-slate-800">Add an image or GIF</div>
                        <div className="mt-1 text-xs font-semibold text-slate-400">
                          JPG, PNG, WEBP or GIF. A wide promotional visual works best.
                        </div>
                      </div>
                      <span className="rounded-xl bg-white px-3 py-2 text-xs font-black text-[#d86a78] shadow-sm ring-1 ring-slate-200">
                        Browse
                      </span>
                      <input
                        key={`add-${mediaInputKey}`}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleMediaChange}
                        className="hidden"
                      />
                    </label>
                  )}

                  <p className="mt-2 text-xs font-semibold text-slate-400">
                    Media is used only for this custom User Notification. Text-only notifications keep the existing behavior.
                  </p>
                </div>

                <div>
                  <label className="text-sm font-black text-slate-800">
                    Delivery time
                  </label>

                  <div className="mt-2 grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setSendMode("now");
                        resetScheduleFields();
                        resetResult();
                      }}
                      className={[
                        "rounded-2xl border px-4 py-3 text-left transition",
                        sendMode === "now"
                          ? "border-[#d86a78] bg-[#fff0f2] text-[#d86a78]"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                      ].join(" ")}
                    >
                      <Send size={18} />
                      <div className="mt-2 text-sm font-black">Send now</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setSendMode("schedule");
                        resetResult();
                      }}
                      className={[
                        "rounded-2xl border px-4 py-3 text-left transition",
                        sendMode === "schedule"
                          ? "border-[#d86a78] bg-[#fff0f2] text-[#d86a78]"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                      ].join(" ")}
                    >
                      <CalendarClock size={18} />
                      <div className="mt-2 text-sm font-black">Schedule</div>
                    </button>
                  </div>

                  {sendMode === "schedule" ? (
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setScheduleType("once");
                            setStartDate("");
                            setEndDate("");
                            setDailyTime("");
                            resetResult();
                          }}
                          className={[
                            "rounded-xl border px-4 py-3 text-left text-sm font-black transition",
                            scheduleType === "once"
                              ? "border-[#d86a78] bg-white text-[#d86a78]"
                              : "border-slate-200 bg-white text-slate-600",
                          ].join(" ")}
                        >
                          One time
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setScheduleType("daily");
                            setScheduledAt("");
                            resetResult();
                          }}
                          className={[
                            "rounded-xl border px-4 py-3 text-left text-sm font-black transition",
                            scheduleType === "daily"
                              ? "border-[#d86a78] bg-white text-[#d86a78]"
                              : "border-slate-200 bg-white text-slate-600",
                          ].join(" ")}
                        >
                          Daily
                        </button>
                      </div>

                      {scheduleType === "once" ? (
                        <div className="mt-3">
                          <label className="text-xs font-black text-slate-500">
                            Send at
                          </label>
                          <input
                            type="datetime-local"
                            value={scheduledAt}
                            onChange={(e) => {
                              setScheduledAt(e.target.value);
                              resetResult();
                            }}
                            className={`${inputClass} mt-2`}
                          />
                        </div>
                      ) : (
                        <div className="mt-3 grid gap-3 sm:grid-cols-3">
                          <div>
                            <label className="text-xs font-black text-slate-500">
                              Start date
                            </label>
                            <input
                              type="date"
                              value={startDate}
                              onChange={(e) => {
                                setStartDate(e.target.value);
                                resetResult();
                              }}
                              className={`${inputClass} mt-2`}
                            />
                          </div>

                          <div>
                            <label className="text-xs font-black text-slate-500">
                              End date
                            </label>
                            <input
                              type="date"
                              value={endDate}
                              onChange={(e) => {
                                setEndDate(e.target.value);
                                resetResult();
                              }}
                              className={`${inputClass} mt-2`}
                            />
                          </div>

                          <div>
                            <label className="text-xs font-black text-slate-500">
                              Daily time
                            </label>
                            <input
                              type="time"
                              value={dailyTime}
                              onChange={(e) => {
                                setDailyTime(e.target.value);
                                resetResult();
                              }}
                              className={`${inputClass} mt-2`}
                            />
                          </div>
                        </div>
                      )}

                      <p className="mt-3 text-xs font-bold text-slate-400">
                        Daily notifications use India time. The same message will be sent once per day in the selected date range.
                      </p>
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  disabled={!canSend || sending}
                  onClick={handleSend}
                  className={[
                    "flex w-full items-center justify-center gap-3 rounded-2xl px-5 py-4 text-sm font-black text-white transition",
                    canSend && !sending
                      ? "bg-[#d86a78] hover:bg-[#c95b69]"
                      : "cursor-not-allowed bg-slate-300",
                  ].join(" ")}
                >
                  {sending ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : sendMode === "schedule" ? (
                    <CalendarClock size={18} />
                  ) : (
                    <Send size={18} />
                  )}

                  {sending
                    ? sendMode === "schedule"
                      ? "Scheduling..."
                      : "Sending..."
                    : sendMode === "schedule"
                      ? "Schedule notification"
                      : "Send notification"}
                </button>
              </div>

              </div>
            </div>

              <div className={`${cardClass} min-w-0 p-4`}>
                <div className="flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-violet-50 text-violet-600"><Eye size={19} /></span>
                  <div><div className="text-[17px] font-black text-[#101b35]">Preview</div><div className="text-[11px] font-semibold text-slate-500">See how your notification will appear.</div></div>
                </div>

                <div className="mt-4 grid grid-cols-2 rounded-full border border-slate-200 bg-slate-50 p-1 text-center text-[10px] font-black"><span className="rounded-full border border-rose-200 bg-white py-2 text-[#e5536c]">Mobile Preview</span><span className="py-2 text-slate-500">Details</span></div>

                <div className="relative mx-auto mt-4 h-[300px] max-w-[280px] overflow-hidden rounded-[34px] border-[7px] border-[#1d2a59] bg-[linear-gradient(160deg,#9db5ee_0%,#d9a9db_48%,#f3c6dc_100%)] shadow-[0_18px_40px_rgba(29,42,89,0.20)]">
                  <div className="absolute left-1/2 top-1.5 h-2 w-14 -translate-x-1/2 rounded-full bg-[#111d43]" />
                  <div className="pt-9 text-center text-white"><div className="text-[36px] font-light leading-none">9:41</div><div className="mt-2 text-[11px] font-semibold">Monday, Jun 10</div></div>
                  {mediaPreview ? (
                    <div className="absolute inset-x-0 bottom-[86px] h-[88px] bg-slate-100">
                      <img
                        src={mediaPreview}
                        alt="Notification preview"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : null}

                  <div className="absolute inset-x-2 bottom-3 flex gap-2.5 rounded-[20px] bg-white/95 p-3 shadow-xl backdrop-blur">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fff0f2] text-[#d86a78]">
                      <Bell size={19} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-black text-slate-950">
                        {title.trim() || "Notification title"}
                      </div>
                      <div className="mt-1 line-clamp-3 text-xs font-semibold leading-5 text-slate-500">
                        {body.trim() || "Your message preview will appear here."}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-4 text-xs font-bold text-slate-500">
                  <div className="flex items-center justify-between">
                    <span>Audience</span>
                    <span className="text-slate-900">
                      {audience === "single" ? "Single user" : "All users"}
                    </span>
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <span>State</span>
                    <span className="text-slate-900">{state || "All states"}</span>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span>Delivery</span>
                    <span className="truncate text-right text-slate-900">
                      {getScheduleText({
                        sendMode,
                        scheduleType,
                        scheduledAt,
                        startDate,
                        endDate,
                        dailyTime,
                      })}
                    </span>
                  </div>

                  {audience === "single" ? (
                    <div className="mt-3 flex items-center justify-between gap-4">
                      <span>Selected user</span>
                      <span className="truncate text-right text-slate-900">
                        {selectedUser ? getUserLabel(selectedUser) : "Not selected"}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
          </section>
        </div>

        <section className={`${cardClass} mt-5 p-5`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-black text-slate-950">
                <Clock3 size={19} />
                Scheduled notifications
              </h2>
              <p className="mt-1 text-sm font-semibold text-slate-500">
                View upcoming, sent, cancelled, failed, and completed scheduled notifications.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={clearingHistory}
                onClick={handleClearHistory}
                className={[
                  "rounded-2xl border border-red-200 bg-white px-4 py-2 text-sm font-black text-red-600 hover:bg-red-50",
                  clearingHistory ? "cursor-not-allowed opacity-60" : "",
                ].join(" ")}
              >
                {clearingHistory ? "Clearing..." : "Clear history"}
              </button>

              <button
                type="button"
                onClick={loadScheduledItems}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-600 hover:bg-slate-50"
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
            <div className="max-h-[460px] overflow-auto">
              <table className="w-full min-w-[980px] text-left text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50 text-xs font-black uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3">Audience</th>
                    <th className="px-4 py-3">State</th>
                    <th className="px-4 py-3">Schedule</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 bg-white">
                  {loadingScheduled ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center font-bold text-slate-400"
                      >
                        Loading scheduled notifications...
                      </td>
                    </tr>
                  ) : scheduledItems.length ? (
                    scheduledItems.map((item) => {
                      const isDaily =
                        String(item.schedule_type || "ONCE").toUpperCase() === "DAILY";

                      return (
                        <tr key={item.id} className="align-top">
                          <td className="px-4 py-3">
                            <div className="flex max-w-[420px] items-start gap-3">
                              {item.payload?.imageUrl ? (
                                <img
                                  src={item.payload.imageUrl}
                                  alt=""
                                  className="h-12 w-16 shrink-0 rounded-xl object-cover ring-1 ring-slate-200"
                                />
                              ) : null}
                              <div className="min-w-0">
                                <div className="font-black text-slate-900">
                                  {item.title}
                                </div>
                                <div className="mt-1 line-clamp-1 text-xs font-semibold text-slate-500">
                                  {item.body}
                                </div>
                                {item.payload?.imageUrl ? (
                                  <div className="mt-1 text-[11px] font-black uppercase tracking-wide text-[#d86a78]">
                                    {item.payload?.mediaKind === "gif" ? "GIF attached" : "Image attached"}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            {item.error_message ? (
                              <div className="mt-1 line-clamp-1 max-w-[360px] text-xs font-bold text-red-600">
                                {item.error_message}
                              </div>
                            ) : null}
                          </td>

                          <td className="px-4 py-3 font-bold text-slate-600">
                            {item.audience === "single"
                              ? item.user_name ||
                                item.user_email ||
                                item.user_phone ||
                                item.user_code ||
                                "Single user"
                              : "All users"}
                          </td>

                          <td className="px-4 py-3 font-bold text-slate-600">
                            {item.state || "All states"}
                          </td>

                          <td className="px-4 py-3 font-bold text-slate-600">
                            {isDaily ? (
                              <div>
                                <div>Daily at {item.daily_time || "-"}</div>
                                <div className="mt-1 text-xs text-slate-400">
                                  {item.start_date || "-"} to {item.end_date || "-"}
                                </div>
                                <div className="mt-1 text-xs text-slate-400">
                                  Next: {formatDateTime(item.next_run_at)}
                                </div>
                                <div className="mt-1 text-xs text-slate-400">
                                  Runs: {item.run_count || 0}
                                </div>
                              </div>
                            ) : (
                              formatDateTime(item.scheduled_at)
                            )}
                          </td>

                          <td className="px-4 py-3">
                            <span
                              className={[
                                "inline-flex rounded-full border px-3 py-1 text-xs font-black",
                                getStatusClass(item.status),
                              ].join(" ")}
                            >
                              {item.status}
                            </span>
                          </td>

                          <td className="px-4 py-3 text-right">
                            {item.status === "PENDING" ? (
                              <button
                                type="button"
                                disabled={cancelingId === item.id}
                                onClick={() => handleCancelScheduled(item.id)}
                                className={[
                                  "inline-flex items-center gap-1 rounded-xl border border-red-200 px-3 py-2 text-xs font-black text-red-600 hover:bg-red-50",
                                  cancelingId === item.id
                                    ? "cursor-not-allowed opacity-60"
                                    : "",
                                ].join(" ")}
                              >
                                {cancelingId === item.id ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <X size={14} />
                                )}
                                Cancel
                              </button>
                            ) : (
                              <span className="text-xs font-bold text-slate-400">
                                No action
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center font-bold text-slate-400"
                      >
                        No scheduled notifications found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
